import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fetchAdminContent, updateContent } from "@/lib/apiClient";

const inputCls = "bg-[hsl(220_30%_8%)] border-border focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]";

const fields = [
  { key: "hero_eyebrow", label: "Hero eyebrow", type: "input", hint: "Small label above the headline" },
  { key: "hero_title", label: "Hero title", type: "input", hint: "Main headline (first part)" },
  { key: "hero_title_highlight", label: "Hero title highlight", type: "input", hint: "Highlighted (teal) part of the headline" },
  { key: "hero_subtitle", label: "Hero subtitle", type: "textarea", hint: "Supporting paragraph under the headline" },
  { key: "about_mission", label: "About — mission", type: "textarea" },
  { key: "about_story", label: "About — rebrand story", type: "textarea" },
];

export default function ContentPanel() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAdminContent()
      .then((d) => setForm(d || {}))
      .catch(() => toast.error("Failed to load content."))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(fields.map((f) => [f.key, form[f.key] || ""]));
      await updateContent(payload);
      toast.success("Content saved. Changes are live on the site.");
    } catch {
      toast.error("Failed to save content.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Page content</h2>
        <p className="mt-1 text-sm text-muted-foreground">Edit the homepage hero and About page copy. Changes go live immediately.</p>
      </div>
      <div className="space-y-5 rounded-[var(--radius-card)] border border-border bg-card p-6">
        {fields.map((f) => (
          <div key={f.key} className="space-y-2">
            <Label htmlFor={f.key} className="text-sm">{f.label}</Label>
            {f.type === "textarea" ? (
              <Textarea id={f.key} rows={3} value={form[f.key] || ""} onChange={set(f.key)} className={inputCls} data-testid={`content-${f.key}-input`} />
            ) : (
              <Input id={f.key} value={form[f.key] || ""} onChange={set(f.key)} className={inputCls} data-testid={`content-${f.key}-input`} />
            )}
            {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="content-save-button">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : <><Save className="mr-2 h-4 w-4" /> Save changes</>}
        </Button>
      </div>
    </div>
  );
}
